import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboardFilters } from '../Widgets/filters/useDashboardFilters';
import DashboardFilterBar from '../Widgets/filters/DashboardFilterBar';
import { useTheme } from '../hooks/useTheme';
import TenantProfileCard from './TenantProfileCard';
import TenantKpiStrip from './TenantKpiStrip';
import TenantYoYChart from './TenantYoYChart';
import TenantPeriodTable from './TenantPeriodTable';
import {
    useTenantDetail,
    useTenantLifetimeTotals,
    useTenantPeriodTotals,
    useTenantCustomTotals,
    useTenantSalesComparison,
    useTenantSalesMoM,
    useTenantGrowthAnalysis,
} from './useTenantDashboardData';
import './tenant-dashboard.css';

/**
 * V3TenantDashboardPage — Sprint 3.9
 *
 * Top-level route component for /v3/tenant. Reads the canonical filter state
 * from DashboardFiltersProvider and drives every section from the first
 * selected tenant id. Multi-tenant compare is out of scope for this sprint —
 * only filters.tenantIds[0] is used by the page.
 */
export default function V3TenantDashboardPage() {
    const { t } = useTranslation();
    const { filters, filtersReady } = useDashboardFilters();
    const { isDark } = useTheme();

    const tenantId = (filters.tenantIds || [])[0] || '';
    const propertyIds = filters.propertyIds || [];
    const dateRange = filters.dateRange || null;

    // Tag body for V3 backdrop (matches what AnalyticsPro etc. do).
    useEffect(() => {
        document.body.classList.add('v3-themed');
        return () => {
            document.body.classList.remove('v3-themed');
        };
    }, []);

    const detail = useTenantDetail(tenantId);
    const lifetime = useTenantLifetimeTotals(tenantId);
    const period = useTenantPeriodTotals(tenantId, propertyIds);
    const custom = useTenantCustomTotals(tenantId, propertyIds, dateRange);
    const yoy = useTenantSalesComparison(tenantId, propertyIds);
    const mom = useTenantSalesMoM(tenantId, propertyIds);
    const growth = useTenantGrowthAnalysis(tenantId, '');

    const hasTenant = !!tenantId && filtersReady;

    return (
        <div className="v3 v3-tenant-dash">
            <div className="v3-tenant-dash-header">
                <div>
                    <h1>{t('Tenant Dashboard')}</h1>
                    <p className="sub">
                        {t('Performance overview for a single tenant — pick one from the filter bar.')}
                    </p>
                </div>
            </div>

            <DashboardFilterBar showTenantChip singleTenant />

            {!hasTenant ? (
                <div className="v3-td-empty">
                    <div className="v3-td-arrow" aria-hidden>↑</div>
                    <h3>{t('Select a tenant to begin')}</h3>
                    <p>
                        {t('Use the')} <strong>{t('Tenants')}</strong> {t('chip in the filter bar above to pick a tenant.')}{' '}
                        {t('Property and date filters narrow the search and the comparison window.')}
                    </p>
                </div>
            ) : (
                <>
                    {detail.error && (
                        <div className="v3-td-error v3-td-section">
                            {t('Unable to load tenant profile')} — {detail.error}
                            <button type="button" onClick={detail.refetch}>{t('Retry')}</button>
                        </div>
                    )}

                    {detail.loading && !detail.data ? (
                        <div className="v3-td-card v3-td-section">
                            <div className="v3-td-skeleton" style={{ height: 24, width: 240, marginBottom: 12 }} />
                            <div className="v3-td-skeleton" style={{ height: 140 }} />
                        </div>
                    ) : detail.data ? (
                        <TenantProfileCard
                            tenant={detail.data}
                            liveDays={custom.data?.live_days}
                            lifetime={lifetime.data}
                            lifetimeLoading={lifetime.loading}
                        />
                    ) : null}

                    <TenantKpiStrip
                        periodTotals={period.data}
                        periodLoading={period.loading}
                        periodError={period.error}
                        onRetryPeriod={period.refetch}
                        customTotals={custom.data}
                        customLoading={custom.loading}
                        customError={custom.error}
                        onRetryCustom={custom.refetch}
                        customRange={dateRange}
                    />

                    <TenantYoYChart
                        data={yoy.data}
                        loading={yoy.loading}
                        error={yoy.error}
                        onRetry={yoy.refetch}
                        themeKey={isDark ? 'dark' : 'light'}
                    />

                    <TenantPeriodTable
                        mode="yoy"
                        data={growth.data}
                        loading={growth.loading}
                        error={growth.error}
                        onRetry={growth.refetch}
                    />

                    <TenantPeriodTable
                        mode="mom"
                        data={mom.data}
                        loading={mom.loading}
                        error={mom.error}
                        onRetry={mom.refetch}
                    />
                </>
            )}
        </div>
    );
}
